import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileArrowUp, MagicWand, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { parseAutorizacion, applyAutorizacion } from "@/lib/api";

const FIELD_LABELS = {
  numero_expediente: "Número de Expediente",
  numero_oficio: "Número de Oficio",
  fecha_oficio: "Fecha del Oficio",
  fecha_vencimiento: "Fecha de Vencimiento",
  plazo_dias: "Plazo (días)",
  departamento_drmc: "Departamento DRMC",
  propietario_nombre: "Propietario / Cliente",
  propietario_rnc_cedula: "RNC / Cédula",
  tipo_operacion: "Tipo de Operación",
  agrimensor_nombre: "Agrimensor autorizado",
  agrimensor_codia: "Codia",
  area_derecho: "Área de Derecho (m²)",
  certificado_titulo: "Certificado de Título",
  provincia: "Provincia",
  municipio: "Municipio",
  distrito_municipal: "Distrito Municipal",
  seccion: "Sección",
  lugar_sitio: "Lugar / Sitio",
  parcela: "Parcela / Solar",
  solar: "Solar (urbana)",
  manzana: "Manzana",
  distrito_catastral: "D.C.",
  tipo_designacion: "Tipo Designación",
};

export default function OficioUpload({ expId, onApplied }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [open, setOpen] = useState(false);

  const onPick = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset
    if (!file) return;
    if (!/pdf$/i.test(file.name)) {
      toast.error("Solo se aceptan PDFs");
      return;
    }
    setBusy(true);
    toast.info("Procesando oficio con IA…", { duration: 4000 });
    try {
      const res = await parseAutorizacion(expId, file);
      setExtracted(res.extracted);
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo procesar el oficio");
    } finally { setBusy(false); }
  };

  const updField = (k, v) => setExtracted({ ...extracted, [k]: v });

  const onApply = async () => {
    setBusy(true);
    try {
      await applyAutorizacion(expId, extracted);
      toast.success("Datos aplicados al expediente");
      setOpen(false);
      setExtracted(null);
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error aplicando datos");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        data-testid="upload-oficio-btn"
        size="sm"
        variant="outline"
        onClick={onPick}
        disabled={busy}
        className="rounded-sm gap-1.5"
      >
        <MagicWand weight="duotone" size={14} />
        {busy ? "Procesando…" : "Cargar Oficio de Autorización"}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={onFileChange}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle weight="duotone" size={20} className="text-emerald-500" />
              Datos extraídos del oficio
            </DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              Revisa y edita si hace falta. Al aplicar, solo se rellenan los campos vacíos del expediente (no sobrescribimos lo que ya tienes).
            </p>
          </DialogHeader>
          {extracted && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
                <b>Tip:</b> Los campos con borde <span className="text-emerald-700 font-bold">verde</span> los detectó la IA. Los de borde <span className="text-red-600 font-bold">rojo</span> quedaron vacíos — complétalos manualmente antes de aplicar si los conoces.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 py-2">
                {Object.entries(FIELD_LABELS).map(([k, label]) => {
                  const isEmpty = !extracted[k] || extracted[k].trim() === "";
                  return (
                    <div key={k}>
                      <Label className="coord-label">{label}</Label>
                      <Input
                        data-testid={`extracted-${k}`}
                        value={extracted[k] || ""}
                        onChange={(e) => updField(k, e.target.value)}
                        className={`mt-1 rounded-sm font-mono text-sm border-2 ${
                          isEmpty
                            ? "border-red-300 bg-red-50 focus-visible:border-red-500"
                            : "border-emerald-300 bg-emerald-50 focus-visible:border-emerald-500"
                        }`}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="rounded-sm"
            >
              <X size={14} className="mr-1" />Cancelar
            </Button>
            <Button
              data-testid="apply-oficio-btn"
              onClick={onApply}
              disabled={busy}
              className="rounded-sm bg-orange-500 hover:bg-orange-400 text-white"
            >
              <FileArrowUp weight="duotone" size={14} className="mr-1.5" />
              {busy ? "Aplicando…" : "Aplicar al expediente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
